import { type PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useAuth } from '../features/auth/AuthProvider';
import { useLocale } from '../features/locale/LocaleProvider';
import { Icon } from './Icon';
import { NotificationBell } from './NotificationBell';
import { Sidebar } from './Sidebar';
import { UserAvatar } from './UserAvatar';
import styles from './AppShell.module.scss';

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'waf-crm-sidebar-collapsed';

export function AppShell({ children }: PropsWithChildren) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const { locale, setLocale } = useLocale();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true',
  );

  function toggleSidebarCollapsed() {
    setSidebarCollapsed((current) => {
      const next = !current;
      window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(next));
      return next;
    });
  }

  return (
    <div className={styles.layout}>
      <Sidebar
        open={sidebarOpen}
        collapsed={sidebarCollapsed}
        onClose={() => setSidebarOpen(false)}
        onToggleCollapsed={toggleSidebarCollapsed}
      />

      <div className={`${styles.main} ${sidebarCollapsed ? styles.sidebarCollapsed : ''}`}>
        <header className={styles.appBar}>
          <div className={styles.toolbar}>
            <div className={styles.toolbarLeft}>
              <button
                className={styles.menuBtn}
                aria-label={t('common.appBarMenu')}
                onClick={() => setSidebarOpen(true)}
              >
                <Icon name="menu" size={18} />
              </button>
            </div>

            <div className={styles.userPill}>
              <NotificationBell />
              <div className={styles.localeWrap}>
                <select
                  className={styles.localeSelect}
                  aria-label={t('common.language')}
                  value={locale}
                  onChange={(event) => setLocale(event.target.value as 'uk' | 'en')}
                >
                  <option value="uk">{t('common.ukrainian')}</option>
                  <option value="en">{t('common.english')}</option>
                </select>
              </div>
              <Link
                to="/profile"
                className={styles.profileLink}
                aria-label={t('common.openProfile')}
              >
                <UserAvatar user={user ?? { full_name: '', avatar_url: null }} size={28} />
                <span className={styles.userInfo}>
                  <span className={styles.userName}>{user?.full_name}</span>
                  <span className={styles.userRoles}>{user?.roles.join(', ')}</span>
                </span>
              </Link>
              <div className={styles.divider} />
              <button className={styles.signOutBtn} onClick={() => void logout()}>
                <Icon name="logout" size={14} />
                {t('common.signOut')}
              </button>
            </div>
          </div>
        </header>

        <main className={styles.content}>{children}</main>
      </div>
    </div>
  );
}
